import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { saveSignupDraft } from '../utils/signupDraft';
import { PREVIEW_KEY, pendingPreview } from '../utils/businessPreview';

export default function KeepAgentPage() {
  const [saved] = useState(pendingPreview);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const user = useAuthStore((state) => state.user);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Keep your business agent · Serviti AI';
    if (!saved) { setError('This preview link has expired. You can still start a trial.'); return undefined; }
    let active = true;
    api.get('/business-preview/draft', { headers: { 'X-Preview-Token': saved.token } }).then(({ data }) => {
      if (!active) return;
      setDraft(data);
      saveSignupDraft({ businessName: data.business_name, description: data.content, website: '', noWebsite: true });
      // Contact prefill is separate from business content, and scoped to the
      // same expiring preview capability. It is never an auth credential.
      try { sessionStorage.setItem(PREVIEW_KEY, JSON.stringify({ ...saved, email: data.email })); } catch { /* Optional storage. */ }
    }).catch((err) => { if (active) setError(err.response?.data?.detail || 'Could not load your preview. Please retry.'); });
    return () => { active = false; };
  }, [saved]);

  const keep = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await api.post('/business-preview/claim', {}, { headers: { 'X-Preview-Token': saved.token } });
      sessionStorage.removeItem(PREVIEW_KEY);
      navigate(`/t/${data.tenant_id}/get-started`, { replace: true });
    } catch (err) { setError(err.response?.data?.detail || 'Your agent could not be saved. Your preview is still available; please retry.'); }
    finally { setSaving(false); }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12">
      <section className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <img src="/serviti_logo_dark.png" alt="Serviti AI" className="mb-8 h-auto w-40" />
        <h1 className="text-2xl font-bold text-slate-950">Keep this agent answering your customers.</h1>
        {error && <p role="alert" className="my-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        {draft && <>
          <h2 className="mt-6 font-semibold text-slate-900">{draft.business_name}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">Your pasted business information is ready. Save it to your workspace and publish your agent without pasting it again.</p>
          <details className="my-5 rounded-lg border border-slate-200 p-3"><summary className="cursor-pointer text-sm font-semibold">Review your business information</summary><p className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-600">{draft.content}</p></details>
          {authenticated && user?.tenant_id ? (
            <button type="button" onClick={keep} disabled={saving} className="mt-3 w-full rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white disabled:opacity-50">{saving ? 'Saving your agent…' : 'Save agent to my workspace'}</button>
          ) : (
            <div className="space-y-3">
              <Link to="/auth/signup" className="block rounded-lg bg-blue-700 px-4 py-3 text-center font-semibold text-white">Create my free trial</Link>
              <Link to="/auth/login" className="block text-center text-sm font-semibold text-blue-700">Already have an account? Sign in</Link>
              <p className="text-sm text-slate-500">30-day Growth trial. No card required. You’ll review the terms during signup.</p>
            </div>
          )}
        </>}
        {!draft && !error && <p className="mt-4 text-slate-600">Loading your preview…</p>}
        {error && <Link to="/auth/signup" className="mt-5 inline-block font-semibold text-blue-700">Continue to signup</Link>}
      </section>
    </main>
  );
}
